import React from 'react';
import { am5, am5themes_Animated, am5xy, mui } from '@local/finsera-core/src/libs';
import * as types from '@local/finsera-core/src/types';

import { createAxes } from './methods/create-axes';
import { createSeries } from './methods/create-series';
import { createDownloadButton } from '../shared/create-download-button';
import { handleZoom } from '../shared/handle-zoom';
import { useChartOnReady } from '../shared/use-chart-on-ready';

export type ChartRef = {
  series: any[] | null;
  seriesKeys: string[];
  onReady: (_callback: (_series: any[]) => void) => void;
};

export type ChartProps = {
  id: string;
  data: types.widgets.TableData;
  fullScreen: boolean;

  xAxisField: string;
  innerBar: string;
  innerBarTitle: string;
  outerBar: string;
  outerBarTitle: string;
  line: string;
  lineTitle: string;

  leftTitle: string;
  rightTitle: string;

  download: boolean;
  customDownloadFormatting: (_data: types.widgets.TableData) => types.widgets.TableData;
  seriesColorByName: Record<string, string>;
};

const Chart = React.forwardRef<ChartRef, ChartProps>(
  (
    {
      id,
      data,
      fullScreen,
      xAxisField,
      download,
      leftTitle,
      rightTitle,
      innerBar,
      innerBarTitle,
      outerBar,
      outerBarTitle,
      line,
      lineTitle,
      customDownloadFormatting,
      seriesColorByName,
    },
    ref
  ) => {
    const widgetId = `chart-${id}-${fullScreen ? 'fs' : 'sw'}`;
    const seriesKeys = [innerBarTitle, outerBarTitle, lineTitle];
    const [seriesState, setSeriesState] = React.useState<any[] | null>(null);
    const { registerCallback, executeCallbacks } = useChartOnReady();

    React.useImperativeHandle(
      ref,
      () => ({
        series: seriesState,
        seriesKeys,
        onReady: registerCallback,
      }),
      [seriesState, seriesKeys, registerCallback]
    );

    React.useEffect(() => {
      // Create root element
      const root = am5.Root.new(widgetId);

      // Remove amCharts logo
      root._logo?.dispose();

      // Set themes
      root.setThemes([am5themes_Animated.new(root)]);

      // Create chart
      const chart = root.container.children.push(
        am5xy.XYChart.new(root, {
          panX: false,
          panY: false,
          wheelX: 'panX',
          wheelY: 'zoomX',
          pinchZoomX: false,
          layout: root.verticalLayout,
          paddingLeft: download ? 20 : 0,
        })
      );

      // Root settings
      chart.appear(1000, 100);

      const { categoryAxis, valueAxis1, valueAxis2 } = createAxes({
        root,
        chart,
        data,
        xAxisField,
        leftTitle,
        rightTitle,
      });

      const createdSeries = createSeries({
        root,
        chart,
        data,
        categoryAxis,
        valueAxis1,
        valueAxis2,
        xAxisField,
        innerBar,
        innerBarTitle,
        outerBar,
        outerBarTitle,
        line,
        lineTitle,
        valueAxis: valueAxis2,
        seriesColorByName,
      });

      setSeriesState(createdSeries);

      chart.set('cursor', am5xy.XYCursor.new(root, {}));

      handleZoom(chart);
      if (download) createDownloadButton(root, xAxisField, data, customDownloadFormatting);

      // Execute callbacks when chart has actually finished rendering and animating
      executeCallbacks(createdSeries);

      return () => {
        setSeriesState(null);
        root.dispose();
      };
    }, [data, seriesColorByName]);

    return (
      <mui.core.Box
        sx={{
          width: '100%',
          height: '100%',
        }}
      >
        <div
          id={widgetId}
          style={{
            width: '100%',
            height: '100%',
          }}
        />
      </mui.core.Box>
    );
  }
);

export default Chart;
